import { Component } from '@theme/component';
import { fetchConfig, resetShimmer } from '@theme/utilities';
import { sectionRenderer } from '@theme/section-renderer';
import { StandardEvents } from '@shopify/events';
import { fetchVisibleCartItemCount, updateAllCartIcons } from '@theme/keeply-cart-count';

const OPT_OUT_KEY = 'keeply-prio-plus-opt-out';

/**
 * Cart drawer Prio+ upsell — adds/removes a configured variant and auto-selects by default.
 *
 * Important: this component intentionally does NOT dispatch a CartLinesUpdateEvent and does
 * NOT morph the cart section. A full section morph re-renders the customer line items from
 * server HTML, which wipes any client-side injections from third-party personalizer apps
 * (replaced design image + "view design" link). Instead we mutate the cart via AJAX and
 * surgically patch only the totals block, leaving every line item untouched.
 *
 * @extends {Component}
 */
class CartDrawerPrioPlus extends Component {
  /** @type {boolean} */
  #isUpdating = false;

  connectedCallback() {
    super.connectedCallback();
    this.addEventListener('click', this.#onClick);
    document.addEventListener(StandardEvents.cartLinesUpdate, this.#handleCartLinesUpdate);
    document.addEventListener('cart:updated', this.#handleExternalCartUpdate);
    document.addEventListener('cart:refresh', this.#handleExternalCartUpdate);
    this.closest('theme-drawer')?.addEventListener('theme-drawer:open', this.#handleDrawerOpen);
    this.#scheduleAutoAdd();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.removeEventListener('click', this.#onClick);
    document.removeEventListener(StandardEvents.cartLinesUpdate, this.#handleCartLinesUpdate);
    document.removeEventListener('cart:updated', this.#handleExternalCartUpdate);
    document.removeEventListener('cart:refresh', this.#handleExternalCartUpdate);
    this.closest('theme-drawer')?.removeEventListener('theme-drawer:open', this.#handleDrawerOpen);
  }

  #handleExternalCartUpdate = () => {
    this.#scheduleAutoAdd();
  };

  #scheduleAutoAdd() {
    requestAnimationFrame(() => {
      requestAnimationFrame(async () => {
        await this.#syncInCartState();
        this.#syncToggleState();
        if (this.#isDrawerOpen()) {
          await this.#maybeAutoAdd();
        }
      });
    });
  }

  #handleDrawerOpen = async () => {
    this.#scheduleAutoAdd();
  };

  /** @param {Event} event */
  #onClick = (event) => {
    const target = event.target instanceof Element ? event.target.closest('[data-prio-plus-toggle]') : null;
    if (!target || !this.contains(target)) return;
    event.preventDefault();
    this.#onToggleClick(/** @type {HTMLElement} */ (target));
  };

  /**
   * @param {HTMLElement} toggle
   */
  async #onToggleClick(toggle) {
    if (this.#isUpdating) return;

    const isChecked = toggle.getAttribute('aria-checked') === 'true';

    if (!isChecked) {
      if (this.dataset.hasProduct !== 'true') return;
      sessionStorage.removeItem(OPT_OUT_KEY);
      await this.#addPrioPlus();
    } else {
      sessionStorage.setItem(OPT_OUT_KEY, '1');
      await this.#removePrioPlus();
    }
  }

  /**
   * Re-sync toggle state when the cart is changed by another component (e.g. a product is
   * added or removed). We never re-add here to avoid racing with the section morph that the
   * other component performs — auto-add is handled on connect / drawer open instead.
   *
   * @param {CustomEvent & { promise?: Promise<any> }} event
   */
  #handleCartLinesUpdate = (event) => {
    event.promise
      ?.then(async () => {
        if (this.#cartHasOnlyPrioOrEmpty()) {
          sessionStorage.removeItem(OPT_OUT_KEY);
        }
        this.#scheduleAutoAdd();
      })
      .catch(() => {});
  };

  #cartHasOnlyPrioOrEmpty() {
    const rows = document.querySelectorAll('#cart-drawer .cart-items__table-row');
    if (!rows.length) return true;

    let nonPrioCount = 0;
    rows.forEach((row) => {
      if (row instanceof HTMLElement && row.dataset.prioPlus !== 'true') nonPrioCount += 1;
    });
    return nonPrioCount === 0;
  }

  #shouldBeChecked() {
    if (sessionStorage.getItem(OPT_OUT_KEY)) return false;
    if (this.#isInCart()) return true;
    if (this.#isUpdating) return this.#isDefaultOn();
    if (this.#isDefaultOn() && this.dataset.hasRegularItems === 'true') return true;
    return false;
  }

  #syncToggleState() {
    const toggle = this.querySelector('[data-prio-plus-toggle]');
    if (!toggle) return;
    toggle.setAttribute('aria-checked', this.#shouldBeChecked() ? 'true' : 'false');
  }

  async #maybeAutoAdd() {
    if (this.#isUpdating) return;
    if (!this.#isEnabled()) return;
    if (!this.#isDefaultOn()) return;
    if (this.dataset.hasProduct !== 'true') return;
    if (sessionStorage.getItem(OPT_OUT_KEY)) return;
    if (this.#isInCart()) return;
    if (!(await this.#cartHasRegularItems())) return;

    await this.#addPrioPlus();
  }

  async #cartHasRegularItems() {
    const prioProductId = Number(this.dataset.productId);

    try {
      const response = await fetch(`${Theme.routes.cart_url}.json`);
      if (!response.ok) return this.dataset.hasRegularItems === 'true';

      const cart = await response.json();
      if (!cart.items?.length) return false;

      if (!prioProductId) {
        return cart.items.length > 0;
      }

      return cart.items.some((item) => item.product_id !== prioProductId);
    } catch {
      return this.dataset.hasRegularItems === 'true';
    }
  }

  #getSectionIds() {
    const sectionIds = new Set();
    const sectionId = this.dataset.sectionId;
    if (sectionId) sectionIds.add(sectionId);

    document.querySelectorAll('cart-items-component').forEach((item) => {
      if (item instanceof HTMLElement && item.dataset.sectionId) {
        sectionIds.add(item.dataset.sectionId);
      }
    });

    return Array.from(sectionIds);
  }

  async #addPrioPlus() {
    const variantId = this.dataset.variantId;
    if (!variantId) return;

    const sectionIds = this.#getSectionIds();

    this.#setLoading(true);

    const formData = new FormData();
    formData.append('id', variantId);
    formData.append('quantity', '1');
    formData.append('properties[_prio_plus]', 'true');
    if (sectionIds.length) {
      formData.append('sections', sectionIds.join(','));
      formData.append('sections_url', window.location.pathname);
    }

    try {
      const fetchCfg = fetchConfig('javascript', { body: formData });
      const response = await fetch(Theme.routes.cart_add_url, {
        ...fetchCfg,
        headers: {
          ...fetchCfg.headers,
          Accept: 'text/html',
        },
      });
      const data = await response.json();

      if (data.status) {
        console.warn('[cart-drawer-prio-plus]', data.message || 'Failed to add Prio+');
        await this.#syncInCartState();
        return;
      }

      this.dataset.inCart = 'true';
      this.dataset.lineKey = data.key || (await this.#findPrioLineKey());
      await this.#refreshCartTotals(data.sections);
      await this.#updateCartBubbleCounts();
    } catch (error) {
      console.warn('[cart-drawer-prio-plus]', error);
      await this.#syncInCartState();
    } finally {
      this.#setLoading(false);
      this.#syncToggleState();
    }
  }

  async #removePrioPlus() {
    let lineKey = this.dataset.lineKey;

    if (!lineKey) {
      lineKey = await this.#findPrioLineKey();
    }
    if (!lineKey) {
      this.dataset.inCart = 'false';
      this.#syncToggleState();
      return;
    }

    const sectionIds = this.#getSectionIds();

    this.#setLoading(true);

    const body = JSON.stringify({
      id: lineKey,
      quantity: 0,
      sections: sectionIds.join(','),
      sections_url: window.location.pathname,
    });

    try {
      const response = await fetch(Theme.routes.cart_change_url, fetchConfig('json', { body }));
      const data = JSON.parse(await response.text());

      if (data.errors) {
        console.warn('[cart-drawer-prio-plus]', data.errors);
        return;
      }

      this.dataset.inCart = 'false';
      this.dataset.lineKey = '';
      await this.#refreshCartTotals(data.sections);
      await this.#updateCartBubbleCounts();
    } catch (error) {
      console.warn('[cart-drawer-prio-plus]', error);
    } finally {
      this.#setLoading(false);
      this.#syncToggleState();
    }
  }

  /**
   * Updates cart subtotal after Prio+ is added or removed. Tries the sections payload from
   * the cart AJAX response first, then re-fetches the cart drawer section, then /cart.js.
   *
   * @param {Record<string, string> | undefined} sections
   */
  async #refreshCartTotals(sections) {
    const updatedFromSections = this.#applyTotalsFromSections(sections);

    if (!updatedFromSections) {
      const sectionId = this.dataset.sectionId;
      if (sectionId) {
        try {
          const html = await sectionRenderer.getSectionHTML(sectionId, false);
          this.#applyTotalsFromSections({ [sectionId]: html });
        } catch (error) {
          console.warn('[cart-drawer-prio-plus]', error);
        }
      }
    }

    await this.#applyTotalsFromCartJson();
    resetShimmer(document.getElementById('cart-drawer') ?? document.body);
  }

  /**
   * Patches only the cart totals (subtotal / discounts) from freshly rendered section HTML,
   * without touching the line items. This preserves third-party personalizer injections.
   *
   * @param {Record<string, string> | undefined} sections
   * @returns {boolean} Whether at least one subtotal element was updated
   */
  #applyTotalsFromSections(sections) {
    if (!sections) return false;

    let updated = false;

    for (const [id, html] of Object.entries(sections)) {
      if (typeof html !== 'string') continue;

      const parsed = new DOMParser().parseFromString(html, 'text/html');
      const newTotals = parsed.querySelector('.cart-totals');
      if (!newTotals) continue;

      /** @type {HTMLElement[]} */
      let containers = [
        document.getElementById(`shopify-section-${id}`),
        document.getElementById('cart-drawer'),
      ].filter((node) => node instanceof HTMLElement);

      if (!containers.length) {
        containers = [document.body];
      }

      for (const container of containers) {
        container.querySelectorAll('.cart-totals').forEach((totalsEl) => {
          if (this.#patchTotalsElement(totalsEl, newTotals)) updated = true;
        });
      }
    }

    return updated;
  }

  /**
   * @param {Element} totalsEl
   * @param {Element} newTotals
   * @returns {boolean}
   */
  #patchTotalsElement(totalsEl, newTotals) {
    const newValue = newTotals.querySelector('[data-cart-subtotal]');
    const currentValue = totalsEl.querySelector('[data-cart-subtotal]');
    if (!newValue || !currentValue) return false;

    const value = newValue.getAttribute('value');
    if (value != null) currentValue.setAttribute('value', value);
    currentValue.textContent = newValue.textContent;

    const newOriginal = newTotals.querySelector('.cart-totals__original-container');
    const currentOriginal = totalsEl.querySelector('.cart-totals__original-container');
    if (newOriginal && currentOriginal) {
      currentOriginal.innerHTML = newOriginal.innerHTML;
    }

    return true;
  }

  async #applyTotalsFromCartJson() {
    try {
      const response = await fetch(`${Theme.routes.cart_url}.json`);
      if (!response.ok) return;

      const cart = await response.json();
      const totalCents = Number(cart.total_price);
      if (!Number.isFinite(totalCents)) return;

      const formatted = this.#formatCartTotal(totalCents, cart.currency);
      const drawer = document.getElementById('cart-drawer');
      if (!drawer) return;

      drawer.querySelectorAll('[data-cart-subtotal]').forEach((el) => {
        el.setAttribute('value', formatted);
        el.textContent = formatted;
      });
    } catch (error) {
      console.warn('[cart-drawer-prio-plus]', error);
    }
  }

  /**
   * @param {number} totalCents
   * @param {string} [currency]
   * @returns {string}
   */
  #formatCartTotal(totalCents, currency = 'EUR') {
    const locale = document.documentElement.lang?.replace('_', '-') || 'de-DE';
    return new Intl.NumberFormat(locale, {
      style: 'currency',
      currency,
    }).format(totalCents / 100);
  }

  async #syncInCartState() {
    const lineKey = await this.#findPrioLineKey();
    this.dataset.inCart = lineKey ? 'true' : 'false';
    this.dataset.lineKey = lineKey;
  }

  async #updateCartBubbleCounts() {
    const prioProductId = Number(this.dataset.productId);
    const count = await fetchVisibleCartItemCount(prioProductId);
    updateAllCartIcons(count, true);
  }

  async #findPrioLineKey() {
    const productId = Number(this.dataset.productId);
    if (!productId) return '';

    try {
      const response = await fetch(`${Theme.routes.cart_url}.json`);
      if (!response.ok) return '';
      const cart = await response.json();
      const match = cart.items?.find((item) => item.product_id === productId);
      return match?.key ?? '';
    } catch {
      return '';
    }
  }

  #isEnabled() {
    return this.dataset.enabled === 'true';
  }

  #isInCart() {
    return this.dataset.inCart === 'true';
  }

  #isDefaultOn() {
    return this.dataset.defaultOn === 'true';
  }

  #isDrawerOpen() {
    const dialog = this.closest('dialog');
    return dialog instanceof HTMLDialogElement ? dialog.open : false;
  }

  /** @param {boolean} loading */
  #setLoading(loading) {
    this.#isUpdating = loading;
    this.classList.toggle('is-loading', loading);
  }
}

if (!customElements.get('cart-drawer-prio-plus')) {
  customElements.define('cart-drawer-prio-plus', CartDrawerPrioPlus);
}
